// src/views/Admin/PaymentAyasa.jsx

import React, { Fragment, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { UrlServicesContext } from "components/UrlServicesContext";

import { makeStyles } from "@mui/styles";
import axios from "axios";
import Moment from "moment";
import {
  Grid,
  Toolbar,
  Alert,
  Backdrop,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
} from "@mui/material";

import {
  AppBarStyle,
  cardBodyStyle,
  ButtonStyle0,
} from "assets/jss/material-dashboard-react/components/cardBodyStyle";

import logoUsame from "assets/img/logo-Usame-web-color.png";
import noDataImg from "assets/img/undraw_House_searching_re_stk8.png";
import loaderGif from "assets/img/loading.gif";
import errorImg from "assets/img/error.png";
import successImg from "assets/img/success.gif";
import pendingImg from "assets/img/pending.png";
import typeCard from "assets/img/marcas-tarjetas.png";

const useStyles = makeStyles(cardBodyStyle);

const NAVY = "#0B2D5C";
const ORANGE = "#FF7A1A";

const listStyle = {
  py: 0,
  width: "100%",
  maxWidth: 420,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  backgroundColor: "background.paper",
};

// Debe coincidir con los productsId usados al registrar (ver ProductsPage.js)
const PLAN_BY_PRODUCT_ID = {
  5: { name: "Plan Plus", price: 240 },
  6: { name: "Plan Esencial", price: 204 },
};

export default function PaymentAyasa() {
  const classes = useStyles();
  const { slug } = useParams();
  const { urlServices, LightboxPlaceToPay } = useContext(UrlServicesContext);

  const [loader, setLoader] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expired, setExpired] = useState(false);
  const [mensageSystem, setMensageSystem] = useState("");
  const [record, setRecord] = useState(null);

  const [phase, setPhase] = useState("info"); // info | loading | result
  const [disabled, setDisabled] = useState(false);
  const [payError, setPayError] = useState("");
  const [result, setResult] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDataPolicy, setAcceptedDataPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const plan = record ? PLAN_BY_PRODUCT_ID[record.productsId] : null;
  const ivaPercent = 0.15;
  const basePrice = plan?.price || 0;
  const subTotal = parseFloat((basePrice / (1 + ivaPercent)).toFixed(2));
  const ivaTotal = parseFloat((basePrice - subTotal).toFixed(2));
  const payTotal = basePrice;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Cargar Lightbox script
  useEffect(() => {
    if (!LightboxPlaceToPay) return;
    const script = document.createElement("script");
    script.src = LightboxPlaceToPay;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [LightboxPlaceToPay]);

  const fetchRecord = async () => {
    setLoader(true);
    try {
      const res = await axios.get(
        `${urlServices}external-supplier-pay/slug/${slug}`,
      );
      setRecord(res.data);

      // Si ya fue aprobado antes, mostramos directamente el resultado
      if (res.data.status === 1) {
        setResult({
          status: "APPROVED",
          reference:
            res.data.paymentSummary?.reference || res.data.externalSupplierId,
          date: res.data.paymentSummary?.date
            ? Moment(res.data.paymentSummary.date).format("DD-MM-YYYY")
            : "—",
          price: res.data.paymentSummary?.amount || null,
          receipt: res.data.paymentSummary?.receipt || null,
        });
        setPhase("result");
      }

      // Si el enlace caducó (48h sin pagar), no se muestra el formulario de pago
      if (res.data.status === 2) {
        setExpired(true);
      }
    } catch (e) {
      setMensageSystem(
        e.response?.status === 404
          ? "El enlace no es válido o el registro no existe."
          : "Error al consultar la información. Intenta nuevamente.",
      );
      setNotFound(true);
    } finally {
      setLoader(false);
    }
  };

  const openLightbox = (processUrl, reqId) => {
    if (!window.P) return;
    window.P.on("response", () => {
      consultarPago(reqId);
    });
    window.P.init(processUrl);
  };

  const handlePay = async () => {
    if (!acceptedTerms || !acceptedDataPolicy || !record) return;
    setDisabled(true);
    setPayError("");
    try {
      const res = await axios.post(
        `${urlServices}external-supplier-pay/create-session`,
        {
          externalSupplierId: record.externalSupplierId,
          subTotal,
          ivaTotal,
          payTotal,
          acceptedTerms: true,
        },
      );
      openLightbox(res.data.processUrl, res.data.requestId);
    } catch (e) {
      if (e.response?.data?.status === 2) {
        setExpired(true);
      } else {
        setPayError("Error al iniciar el pago. Intenta nuevamente.");
      }
      setDisabled(false);
    }
  };

  const consultarPago = async (reqId) => {
    setPhase("loading");
    try {
      const res = await axios.post(
        `${urlServices}external-supplier-pay/view-pay`,
        {
          requestId: reqId,
          externalSupplierId: record.externalSupplierId,
        },
      );

      const statusStr = res.data.status;
      const data = res.data.data;
      const payment = data.payment?.[0];

      setResult({
        status: statusStr,
        reference:
          payment?.reference || data.request?.payment?.reference || "—",
        date: Moment(payment?.status?.date || data.status?.date).format(
          "DD-MM-YYYY",
        ),
        price: data.request?.payment?.amount?.total || payTotal,
        receipt: payment?.receipt || null,
      });
      setPhase("result");

      if (statusStr !== "PENDING") {
        await sendPaymentEmail();
      }
      if (statusStr !== "APPROVED") {
        setDisabled(false);
      }
    } catch (e) {
      setPayError("Error al consultar el resultado del pago.");
      setPhase("info");
      setDisabled(false);
    }
  };

  const sendPaymentEmail = async () => {
    try {
      await axios.post(
        `${urlServices}external-supplier-pay/send-payment-email`,
        { externalSupplierId: record.externalSupplierId },
      );
    } catch (e) {
      console.error("Error enviando correo de pago:", e.message);
    }
  };

  const handleRetry = async () => {
    setDisabled(true);
    setPayError("");
    try {
      const res = await axios.post(
        `${urlServices}external-supplier-pay/retry-pay`,
        {
          externalSupplierId: record.externalSupplierId,
          subTotal,
          ivaTotal,
          payTotal,
        },
      );
      setPhase("info");
      setResult(null);
      setDisabled(false);
      openLightbox(res.data.processUrl, res.data.requestId);
    } catch (e) {
      setPayError("Error al reintentar el pago.");
      setDisabled(false);
    }
  };

  const statusColor =
    result?.status === "APPROVED"
      ? "success"
      : result?.status === "PENDING"
        ? "warning"
        : "error";
  const statusImg =
    result?.status === "APPROVED"
      ? successImg
      : result?.status === "PENDING"
        ? pendingImg
        : errorImg;
  const statusMsg =
    result?.status === "APPROVED"
      ? "Tu pago ha sido APROBADO exitosamente"
      : result?.status === "PENDING"
        ? "Tu pago está en estado PENDIENTE"
        : "Tu pago fue RECHAZADO";

  return (
    <Fragment>
      <AppBarStyle position="static">
        <Toolbar sx={{ minHeight: "50px !important" }}>
          <img
            src={logoUsame}
            alt="logoheader"
            style={{ width: 70, marginLeft: "5%" }}
          />
        </Toolbar>
      </AppBarStyle>

      <center>
        <Grid
          container
          spacing={2}
          className={classes.containerProfile}
          sx={{ width: "92%", maxWidth: 640, margin: "30px auto 50px" }}
        >
          {notFound ? (
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {mensageSystem}
              </Alert>
              <img
                alt="loaderGif"
                src={noDataImg}
                style={{ width: 260, marginTop: 30 }}
              />
            </Grid>
          ) : expired ? (
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Este enlace de pago ha caducado. Han pasado más de 48 horas
                desde el registro y ya no es posible completar el pago con este
                enlace.
              </Alert>
              <Typography sx={{ fontSize: 13, color: "#6B7585", mb: 2 }}>
                Por favor comunícate con tu asesor para que genere un nuevo
                registro y te comparta un enlace vigente.
              </Typography>
              <img
                alt="caducado"
                src={noDataImg}
                style={{ width: 260, marginTop: 10 }}
              />
            </Grid>
          ) : record ? (
            <>
              {/* ── FASE: info ─────────────────────────────────────── */}
              {phase === "info" && !expired && (
                <>
                  <Grid item xs={12} sx={{ textAlign: "left", mb: 1 }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 22, color: NAVY }}
                    >
                      Confirma tus datos y realiza el pago
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>

                  {payError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{payError}</Alert>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} sx={{ textAlign: "left" }}>
                    <Typography
                      sx={{ fontWeight: 700, mb: 1, color: NAVY, fontSize: 16 }}
                    >
                      Tus datos
                    </Typography>
                    {[
                      [
                        "Nombre",
                        `${record.nameClient} ${record.lastnameClient}`,
                      ],
                      [
                        "Documento",
                        `${record.typesDocument?.toUpperCase()}: ${record.document}`,
                      ],
                      ["Email", record.emailClient],
                      ["Teléfono", record.phoneClient],
                      ["Placa", record.placaClient],
                      ["Dirección", record.addressClient],
                    ].map(([label, val]) => (
                      <Typography key={label} sx={{ fontSize: 13, mb: 0.5 }}>
                        <b style={{ color: NAVY }}>{label}: </b>
                        {val}
                      </Typography>
                    ))}
                    <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                      Estos datos fueron registrados por tu asesor. Si algo no
                      es correcto, contáctalo antes de continuar con el pago.
                    </Alert>
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ textAlign: "left" }}>
                    <Typography
                      sx={{ fontWeight: 700, mb: 1, color: NAVY, fontSize: 16 }}
                    >
                      Resumen del pago
                    </Typography>
                    <Box sx={{ background: "#F4F6F9", borderRadius: 3, p: 2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>
                        {plan?.name}
                      </Typography>
                      <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                        <b>Subtotal 15%:</b> ${subTotal.toFixed(2)}
                      </Typography>
                      <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                        <b>IVA 15%:</b> ${ivaTotal.toFixed(2)}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography
                        sx={{ fontWeight: 800, fontSize: 18, color: ORANGE }}
                      >
                        TOTAL: ${payTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Grid container>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center" }}>
                          <Box
                            component="img"
                            src="https://static.placetopay.com/placetopay-logo.svg"
                            alt="PlaceToPay"
                            sx={{ width: 140, mb: 1 }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center" }}>
                          <Box
                            component="img"
                            src={typeCard}
                            alt="tarjetas"
                            sx={{ width: 160 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Términos y condiciones — el CLIENTE los acepta aquí */}
                  <Grid item xs={12} sx={{ mt: 1, textAlign: "left" }}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <input
                          type="checkbox"
                          id="terms"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          style={{
                            marginTop: 3,
                            cursor: "pointer",
                            accentColor: NAVY,
                          }}
                        />
                        <label
                          htmlFor="terms"
                          style={{
                            fontSize: 12,
                            color: "#444",
                            cursor: "pointer",
                          }}
                        >
                          He leído y acepto los{" "}
                          <span
                            onClick={() => setShowTerms((prev) => !prev)}
                            style={{
                              color: NAVY,
                              fontWeight: 700,
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            Términos y Condiciones
                          </span>{" "}
                          de contratación del servicio de asistencia vehicular
                          AYASA.
                        </label>
                      </Box>

                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1}
                        sx={{ mt: 1.2 }}
                      >
                        <input
                          type="checkbox"
                          id="dataPolicy"
                          checked={acceptedDataPolicy}
                          onChange={(e) =>
                            setAcceptedDataPolicy(e.target.checked)
                          }
                          style={{
                            marginTop: 3,
                            cursor: "pointer",
                            accentColor: NAVY,
                          }}
                        />
                        <label
                          htmlFor="dataPolicy"
                          style={{
                            fontSize: 12,
                            color: "#444",
                            cursor: "pointer",
                          }}
                        >
                          He leído y acepto las{" "}
                          <span
                            onClick={() => setShowTerms((prev) => !prev)}
                            style={{
                              color: NAVY,
                              fontWeight: 700,
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            Políticas de Protección de Datos Personales
                          </span>
                          .
                        </label>
                      </Box>

                      {showTerms && (
                        <Box
                          sx={{
                            mt: 1.5,
                            maxHeight: 200,
                            overflowY: "auto",
                            fontSize: 11,
                            color: "#555",
                            lineHeight: 1.6,
                            borderTop: "1px solid #e0e0e0",
                            pt: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            fontSize={11}
                            mb={0.5}
                          >
                            TÉRMINOS Y CONDICIONES DEL SERVICIO DE ASISTENCIA
                            VEHICULAR
                          </Typography>
                          <Typography variant="body2" fontSize={11} mb={1}>
                            <br></br>• Todo servicio deberá ser coordinado
                            exclusivamente a través de la central de atención
                            telefónica al número: 096-387-1014. Cualquier
                            atención no autorizada o no registrada por dicho
                            canal no contará con cobertura ni derecho a
                            reembolso.
                            <br></br>• La vigencia del servicio está sujeta al
                            cumplimiento puntual de los pagos por parte del
                            cliente, ya sea en modalidad de cuotas mensuales o
                            pago total anticipado.
                            <br></br>• El contrato podrá mantenerse vigente por
                            hasta un máximo de sesenta (60) días en caso de
                            mora, únicamente respecto de la relación
                            contractual, más no respecto del servicio, el cual
                            permanecerá suspendido hasta la regularización de
                            los pagos.
                            <br></br>• Tiempo de carencia 30 días contados desde
                            la fecha de contratación del servicio.
                            <br></br>• No se cubrirán tratamientos odontológicos
                            ya iniciados al momento de la contratación del
                            servicio.
                            <br></br>• No se cubrirán atenciones fuera de la red
                            de prestadores ni solicitudes de reembolso.
                            <br></br>• No se cubrirán retratamientos, salvo que
                            se indique expresamente lo contrario en el recuadro
                            de coberturas.
                            <br></br>• La adhesión al presente servicio es libre
                            y voluntaria.
                            <br></br>• El Servicio de Contact Center será
                            operado por ÚSAME – INNOVATECH S.A. en calidad de
                            Responsable del Tratamiento de los datos personales
                            conforme lo establecido en la Ley Orgánica de
                            Protección de Datos Personales (LOPDP) y su
                            Reglamento. • El cliente declara que los recursos
                            económicos utilizados provienen de fuentes lícitas.
                            <br></br>• El cliente declara haber sido debidamente
                            informado sobre las condiciones, cobertura, costos,
                            excepciones, vigencia y límites del servicio
                            contratado.
                            <br></br>• El presente contrato es válido en todo el
                            territorio de la República del Ecuador y ampara al
                            titular, sus dependientes y/o afiliados adicionales
                            debidamente registrados.
                            <br></br>• El titular del contrato deberá ser mayor
                            de edad al momento de la firma del mismo.
                            <br></br>• El titular es responsable de mantener sus
                            datos actualizados, para efectos de notificación y
                            comunicación.
                            <br></br>• Para el ingreso de los dependientes y/o,
                            afiliados adicionales, en el plan contratado, la
                            edad mínima para el ingreso es a partir de los seis
                            (6) meses de edad. La permanencia será INDEFINIDA
                            siempre y cuando se mantengan los pagos al día.
                            <br></br>• Cuando el ingreso es del Núcleo familiar
                            (Titular + cónyuge + hijos), no existe límite de
                            beneficiarios.
                            <br></br>• Cuando el ingreso es Titular + Familia
                            (Padres, hermanos y suegros), el número máximo de
                            beneficiarios es de cuatro (4), incluyendo el
                            Titular.
                            <br></br>• Todas las asistencias que no se
                            encuentran dentro del cuadro de cobertura, cuentan
                            con un descuento por parte del prestador y quedan a
                            disposición del cliente el tomar y cancelar su
                            respectivo valor.
                            <br></br>• Todo servicio que NO se encuentre
                            estipulado expresamente en el presente contrato, no
                            será cubierto. Aquellos procedimientos no incluidos
                            en el cuadro de coberturas podrán ser ofrecidos con
                            descuento por parte del prestador, quedando a
                            discreción del CLIENTE su aceptación y pago directo.
                            <br></br>• El presente contrato tiene una vigencia
                            indefinida y se renovará automáticamente mientras el
                            cliente se mantenga al día en sus obligaciones.
                            <br></br>• El CLIENTE podrá dar por terminado el
                            presente contrato en cualquier momento, sin
                            penalidad, mediante solicitud escrita dirigida a
                            ÚSAME INNOVATECH S.A., con una anticipación mínima
                            de treinta (30) días calendario. La solicitud deberá
                            contener firma del titular, copia de cédula y ser
                            enviada a través de los canales oficiales (correo
                            electrónico o entrega física). Una vez recibida y
                            validada, la prestación de servicios se mantendrá
                            hasta el último día del mes calendario en curso, y
                            si existiera un pago anticipado, se devolverá el
                            valor no devengado en forma proporcional, deduciendo
                            los costos financieros y administrativos
                            correspondientes.
                            <br></br>• La terminación del contrato afectará
                            automáticamente a todos los dependientes o afiliados
                            registrados, salvo que alguno de ellos asuma la
                            titularidad conforme a los procedimientos
                            establecidos por ÚSAME INNOVATECH S.A. La empresa
                            confirmará por escrito la finalización del contrato
                            y el estado de cuenta en un plazo máximo de cinco
                            (5) días hábiles desde la recepción válida de la
                            solicitud. No se reconocerán devoluciones por
                            periodos ya transcurridos ni por servicios no
                            utilizados dentro del mes en curso.
                            <br></br>• Autorización de Débito Mensual. El
                            Cliente autoriza expresamente a ÚSAME INNOVATECH
                            S.A., a debitar de manera automática el importe
                            correspondiente a la suscripción o servicio
                            contratado, según el plan elegido, de la cuenta
                            bancaria o tarjeta registrada al momento de la
                            contratación.
                            <br></br>• Este débito se realizará de forma
                            mensual, en la fecha acordada, hasta tanto el
                            cliente decida cancelar el servicio conforme a las
                            políticas de cancelación vigentes. El Cliente
                            declara estar debidamente informado del valor a ser
                            debitado y presta su consentimiento expreso para
                            dicha operación recurrente.
                            <br></br>• Cualquier modificación en el valor del
                            servicio será notificada previamente al cliente,
                            quien tendrá la posibilidad de aceptar los nuevos
                            términos o cancelar el servicio antes de que se
                            efectúe el siguiente débito.
                            <br></br>• Si el cliente presenta información falsa
                            o inexacta en el proceso de contratación, perderá de
                            forma automática e irrevocable el derecho a los
                            servicios, asumiendo responsabilidad civil y/o penal
                            por los perjuicios que dicha conducta genere.
                            <br></br>
                            <br></br>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              fontSize={11}
                              mb={0.5}
                            >
                              POLÍTICAS DE PROTECCIÓN DE DATOS PERSONALES
                            </Typography>
                            <br></br>De conformidad con lo previsto en la Ley
                            Orgánica de Protección de Datos, EL CLIENTE libre,
                            voluntaria, inequívoca, informada y expresamente,
                            consiente y autoriza a ÚSAME INNOVATECH S.A. (ÚSAME)
                            para que, en calidad de responsable, trate sus datos
                            personales, que son entregados para la ejecución de
                            esta relación contractual, como datos
                            identificativos y de contacto. Las finalidades del
                            tratamiento serán: gestión de la relación
                            contractual, facturación del servicio, la atención
                            de solicitud, reclamos y ejercicio de derechos de
                            los titulares.
                            <br></br>
                            <br></br>• Los datos personales podrán ser
                            entregados de forma física o digital, la
                            autenticidad y exactitud de los datos personales, es
                            responsabilidad de EL CLIENTE.
                            <br></br>• EL CLIENTE podrá proporcionar los datos
                            personales de los terceros interesados en acceder al
                            servicio a fin de que sean considerados dentro de la
                            cobertura de este, con la observación de que los
                            terceros afiliados deberán firmar la autorización
                            para el tratamiento de sus datos personales para
                            acceder al servicio prestado por ÚSAME.
                            <br></br>• ÚSAME declara expresamente que cumple a
                            cabalidad con la normativa vigente en materia de
                            protección de datos personales para el tratamiento
                            de los datos personales de sus clientes.
                            <br></br>
                            <br></br>ÚSAME declara expresamente que reconoce los
                            derechos consagrados al titular de los datos
                            personales, en la normativa vigente en materia de
                            protección de datos personales.
                            <br></br>
                            <br></br>ÚSAME declara que en caso de requiriese
                            datos personales adicionales de sus clientes para la
                            prestación efectiva del servicio, solicitará la
                            autorización correspondiente para el tratamiento de
                            la información de carácter personal de éstos.
                            <br></br>
                            <br></br>• ÚSAME se compromete a tratar los datos
                            personales, exclusivamente para las finalidades
                            previamente informadas. La conservación y
                            eliminación y/o devolución de los datos personales
                            se realizará en cumplimiento de los plazos que
                            establece la normativa correspondiente y la Política
                            de Protección de Datos Personales de ÚSAME. Una vez
                            transcurrido el tiempo máximo de conservación,
                            ÚSAME, se compromete a destruir de forma segura los
                            archivos físicos y/o digitales que contengan estos
                            datos personales así como el resultado de cualquier
                            elaboración de los mismos o, en su caso, a
                            devolvérselos a EL CLIENTE.
                            <br></br>• De conformidad con la normativa vigente,
                            ÚSAME asume la obligación de proteger los datos
                            personales a los que accedan con ocasión del
                            contrato del servicio. Como consecuencia de esta
                            obligación legal, entre otras, adoptará las medidas
                            de seguridad de tipo técnico, organizativo y de
                            cualquier otra índole, acorde a la naturaleza y
                            criticidad de la información personal objeto de
                            tratamiento.
                            <br></br>• ÚSAME notificará a los titulares
                            cualquier vulneración de sus datos personales
                            conforme a los plazos establecidos en la LOPDP y su
                            Reglamento.
                            <br></br>• La aceptación del presente contrato
                            conlleva la aceptación expresa de la Política de
                            Protección de Datos Personales de ÚSAME,disponible
                            en su página web oficial.
                            <br></br>
                            <br></br>ÚSAME, tratará los datos personales de EL
                            CLIENTE únicamente dentro del territorio
                            ecuatoriano, sin perjuicio, que elalmacenamiento de
                            esta información pueda ser administrada fuera del
                            territorio ecuatoriano. Esta infraestructura puede
                            ser implementada en servidores físicos o en la nube
                            de proveedores que cuenten con las debidas
                            certificaciones internacionales para el tratamiento
                            de Seguridad de la Información.
                            <br></br>
                            <br></br>Adicionalmente, mediante la presente el
                            titular de los datos declara conocer y aceptar el
                            Aviso de Política de Privacidad de Placetopay en la
                            cual se indican sus derechos, datos tratados y las
                            actividades comerciales de los aliados, así como
                            otros datos de interés respecto a la protección de
                            datos personales, que se encuentran detallados en el
                            sitio web de Placetopay en la ruta
                            https://www.placetopay.com/web/politicas-de-privacidad/
                            , mediante la cual, se podrá solicitar la gestión de
                            derechos o consentimientos.
                            <br></br>
                            <br></br>El Comercio o su delegado notificará sobre
                            las actualizaciones del Aviso de Política de
                            Privacidad antes mencionada por medio de los
                            diferentes canales digitales.
                          </Typography>

                          <Box
                            sx={{ textAlign: "right", mt: 1 }}
                            onClick={() => setShowTerms(false)}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: NAVY,
                                fontWeight: 700,
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              Cerrar ↑
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sx={{ textAlign: "center", mt: 2 }}>
                    <ButtonStyle0
                      onClick={handlePay}
                      disabled={
                        disabled || !acceptedTerms || !acceptedDataPolicy
                      }
                    >
                      {disabled
                        ? "Procesando..."
                        : `Pagar $${payTotal.toFixed(2)}`}
                    </ButtonStyle0>
                  </Grid>
                </>
              )}

              {/* ── FASE: loading ────────────────────────────────────── */}
              {phase === "loading" && (
                <Grid item xs={12} sx={{ textAlign: "center", py: 6 }}>
                  <img src={loaderGif} alt="cargando" style={{ width: 60 }} />
                  <Typography sx={{ mt: 2, color: NAVY }}>
                    Verificando el pago...
                  </Typography>
                </Grid>
              )}

              {/* ── FASE: result ─────────────────────────────────────── */}
              {phase === "result" && result && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <img
                    src={statusImg}
                    alt="estado"
                    style={{
                      width: 130,
                      marginBottom: 16,
                      borderRadius: "50%",
                    }}
                  />
                  <Alert
                    severity={statusColor}
                    icon={false}
                    sx={{ fontSize: 18, mb: 2 }}
                  >
                    {statusMsg}
                  </Alert>

                  <List sx={listStyle}>
                    {[
                      ["Fecha", result.date],
                      ["Referencia", result.reference],
                      ["Valor", result.price ? `$${result.price}` : "—"],
                      ...(result.receipt ? [["Recibo", result.receipt]] : []),
                    ].map(([label, val]) => (
                      <Fragment key={label}>
                        <ListItem>
                          <Grid container>
                            <Grid item xs={5}>
                              <b>{label}</b>
                            </Grid>
                            <Grid item xs={7} sx={{ textAlign: "right" }}>
                              <ListItemText primary={val} />
                            </Grid>
                          </Grid>
                        </ListItem>
                        <Divider component="li" />
                      </Fragment>
                    ))}
                  </List>

                  {result.status === "REJECTED" && (
                    <Box sx={{ mt: 3 }}>
                      <ButtonStyle0 onClick={handleRetry} disabled={disabled}>
                        Reintentar pago
                      </ButtonStyle0>
                    </Box>
                  )}
                </Grid>
              )}
            </>
          ) : null}
        </Grid>
      </center>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Fragment>
  );
}
